import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { FaWindows, FaApple } from 'react-icons/fa';
import { FiBox, FiClock, FiGitBranch, FiLayers, FiPackage } from 'react-icons/fi';
// Host glyphs. Rendered MONOCHROME via the same colour token as every other
// icon on these cards - brand colours would make the download grid louder than
// the card it sits in, and would not survive a theme change.
import { SiAutodesk, SiBlender, SiThreedotjs, SiUnrealengine } from 'react-icons/si';
import { managementService } from './service/management.service';
import { homeService } from '../home/service/home.service';
import { Ikey } from './types.type';
import {
  ManagememtContainer,
  ManagementTable,
  FileLoad,
  PortalButton
} from './components';
import { ButtonContainer } from './components/file-load/button-container.component';
import { EyeLicenseSection } from './components/eye-license-section.component';
import { settingsService } from '../settings/service/settings.service';
import { InformSpan } from '../common/components';
import {
  EYE_EULA_VERSION,
  ROUTER_LINKS,
  SBFRAMEWORK_EULA_VERSION,
  SUBDIVS_EULA_VERSION
} from '../common/consts/app-keys.const';
import { useHasFrameworkAccess } from '../sbframework/feature/use-sbframework-access';
import { EYE_PRODUCT_SLUG } from '../common/hooks/use-product-access';
import { useNxtVersion } from '../sbframework/feature/use-nxt-version';
import packageJson from '../../../package.json';
import { fetchChangelogVersions } from '../common/utils/changelog-versions.util';
import { useDownloadTracker } from '../common/hooks/use-download-tracker';

const pkg = packageJson as {
  touchpose?: { version?: string };
  squarebitSubdivs?: { version?: string };
  squarebitEye?: { version?: string };
  sbframework?: { version?: string };
};

// DB Product.name for the framework. Its LicenseSpring keys arrive through the
// normal key list, but they render inside the framework's own grant-gated card
// rather than the generic per-product one, so the name is filtered out of the
// generic grouping below.
const FRAMEWORK_PRODUCT_NAME = 'Squarebit Framework';

type ProductUi = {
  version?: string;
  changelogPath?: string;
  download: 'touchpose' | 'squarebit-subdivs';
};

// Keyed by the product name returned with each license key (DB Product.name).
const PRODUCT_UI: Record<string, ProductUi> = {
  TouchPose: {
    version: pkg.touchpose?.version,
    changelogPath: '/touchpose/changelog',
    download: 'touchpose'
  },
  'Squarebit Subdivs': {
    version: pkg.squarebitSubdivs?.version,
    changelogPath: '/squarebit-subdivs/changelog',
    download: 'squarebit-subdivs'
  }
};

// The storefront pages are the single place products are bought (pricing +
// consent/EULA flow live there); My Products only links out to them. Keyed by
// DB Product.name so we can hide products the user already owns.
const PRODUCT_PAGES: { name: string; path: string }[] = [
  { name: 'TouchPose', path: ROUTER_LINKS.TOUCHPOSE },
  { name: 'Squarebit Subdivs', path: ROUTER_LINKS.SQUAREBIT_SUBDIVS }
];

/** Download arrow for the per-version pills, matching the changelog's. */
const DownloadArrow = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden="true"
    className="shrink-0 transition-transform group-hover:translate-y-[2px]"
  >
    <path
      d="M7 1.75v7.5M3.5 5.75L7 9.25l3.5-3.5M2.5 12.25h9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SubdivsDownload = () => {
  const { t } = useTranslation();
  return (
    <div>
      <div className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-content-muted-light mb-[13px]">
        {t('management.download')}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-[10px]">
        <ButtonContainer os="win64" product="squarebit-subdivs" label="Windows">
          <FaWindows size={18} color="var(--color-text-muted-alt)" />
        </ButtonContainer>
        <ButtonContainer os="mac" product="squarebit-subdivs" label="macOS">
          <FaApple size={18} color="var(--color-text-muted-alt)" />
        </ButtonContainer>
      </div>
    </div>
  );
};

// Squarebit Subdivs is free to download for everyone, so any logged-in user can
// get the plugin here even without a license key. Owners see the download inside
// their license-key card instead, so this renders only when they don't own one.
const SubdivsFreeCard = ({ version }: { version?: string }) => {
  const { t } = useTranslation();
  return (
    <div className="w-full rounded-[18px] border border-border-dark/[0.11] bg-surface-base shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)] overflow-hidden relative">
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue to-[#6d5fc0] rounded-t-[18px]" />
      <div className="px-4 md:px-[30px] pt-[26px] pb-[22px] border-b border-border-dark/[0.07]">
        <div className="flex items-center gap-[10px] flex-wrap">
          <h2 className="text-[19px] font-display font-bold text-content-dark tracking-tight">
            Squarebit Subdivs
          </h2>
          <span className="inline-flex items-center px-[9px] py-[2px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide uppercase">
            Free
          </span>
          {version && (
            <Link
              to="/squarebit-subdivs/changelog"
              className="inline-flex items-center gap-[5px] px-[9px] py-[2px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide cursor-pointer hover:bg-surface-hover-section transition-colors"
            >
              <span className="w-[5px] h-[5px] rounded-full bg-blue animate-pulse" />
              {t('management.product.version', { version })}
            </Link>
          )}
        </div>
      </div>
      <div className="px-[30px] py-[22px]">
        <SubdivsDownload />
        <p className="mt-[16px] font-body italic text-[12px] text-content-muted-alt leading-[1.5]">
          {t('management.subdivsFreeNote')}
        </p>
      </div>
    </div>
  );
};

// Invite-only Squarebit Eye card — rendered ONLY for accounts holding an
// admin-granted 'squarebit-eye' entitlement, framed exactly like the other
// product cards. The download hits the grant-gated endpoint via fetch so a
// 403 (grant revoked since the page loaded) surfaces as a toast.
// Before the FIRST download the user must explicitly accept the Squarebit Eye
// EULA (clickwrap — checkbox + Accept, recorded server-side against the
// grant), so the download button only appears once acceptance is on record;
// later visits skip the prompt because the server remembers it.
// The platforms an archive can be built for. A variant id is
// "<host>" or "<host>-<platform>", so the suffix is only a platform when it is
// one of these - anything else is part of the host name and stays there. That
// keeps an unrecognised variant from silently losing half its id, which would
// make it unreachable even though the server offered it.
const EYE_PLATFORMS = ['win64', 'macos', 'linux'] as const;
type EyePlatform = (typeof EYE_PLATFORMS)[number];

const EYE_PLATFORM_LABEL: Record<EyePlatform, string> = {
  win64: 'Windows',
  macos: 'macOS',
  linux: 'Linux'
};

// Host display names. Proper nouns, so untranslated - the same choice the
// Framework card already makes for "Windows" and "macOS".
const EYE_HOST_LABEL: Record<string, string> = {
  maya: 'Maya',
  unreal: 'Unreal Engine',
  usd: 'USD',
  blender: 'Blender',
  web: 'Web'
};

// One glyph per host, so the grid is scannable without reading every label.
// Autodesk's mark stands in for Maya (there is no Maya glyph), three.js for
// the web build because that is what it integrates with, and layers for USD
// because a USD delivery is exactly that. The complete bundle keeps the
// package icon it has always had.
const EYE_HOST_ICON: Record<
  string,
  React.ComponentType<{ size?: number; color?: string }>
> = {
  maya: SiAutodesk,
  unreal: SiUnrealengine,
  blender: SiBlender,
  usd: FiLayers,
  web: SiThreedotjs,
  product: FiPackage
};

// The order downloads are offered in. The complete bundle goes LAST: it is the
// minority case (handing a whole delivery to a client), and it used to be the
// only button, which is what hid every per-host archive we publish.
const EYE_HOST_ORDER = ['maya', 'unreal', 'usd', 'blender', 'web', 'product'];

/** Split a variant id into its host and, if it has one, its platform. */
const parseEyeVariant = (
  variant: string
): { host: string; platform: EyePlatform | null } => {
  const dash = variant.lastIndexOf('-');
  if (dash > 0) {
    const suffix = variant.slice(dash + 1) as EyePlatform;
    if ((EYE_PLATFORMS as readonly string[]).includes(suffix)) {
      return { host: variant.slice(0, dash), platform: suffix };
    }
  }
  return { host: variant, platform: null };
};

/** What the browser is running, as a starting guess for the platform choice. */
const detectEyePlatform = (): EyePlatform => {
  if (typeof navigator === 'undefined') return 'win64';
  const ua = navigator.userAgent;
  if (/Mac/i.test(ua)) return 'macos';
  if (/Linux|X11/i.test(ua) && !/Android/i.test(ua)) return 'linux';
  return 'win64';
};

const SquarebitEyeCard = () => {
  const { t } = useTranslation();
  const version = pkg.squarebitEye?.version;

  // Published versions, for the older-builds panel. Not gated on the EULA:
  // the bytes are, and hiding the LIST from someone who has not clicked
  // accept yet would only render an empty panel.
  const versionsQuery = useQuery({
    queryKey: ['eye-versions'],
    queryFn: async () => managementService.getEyeVersions()
  });
  // null means "the newest build"; a string is an explicit rollback.
  const [selectedEyeVersion, setSelectedEyeVersion] = useState<string | null>(null);
  // Chosen ONCE for the card, not per download: nobody wants the Maya build
  // for Windows and for macOS, they want the one their machine runs. Seeded
  // from the browser and only shown when a build actually offers a choice.
  const [eyePlatform, setEyePlatform] = useState<EyePlatform>(detectEyePlatform());
  // Same tracker the framework downloads use. The Eye bundle is ~40 MB pulled
  // through our server before the browser is handed anything to save, which
  // is several seconds of nothing after the click - and ButtonContainer only
  // dimmed itself, with no toast saying why. People click again, and each
  // click was another 40 MB.
  const downloads = useDownloadTracker();
  // Everything except the build the big button already offers.
  // Everything the server will serve, newest first. It has already dropped
  // builds below the licence floor and variants it cannot fingerprint, so
  // anything here is downloadable and none of it is filtered again.
  const eyeVersionOptions = versionsQuery.data?.versions ?? [];
  const currentEyeVersion =
    versionsQuery.data?.currentVersion ?? eyeVersionOptions[0]?.version ?? null;
  const activeEyeVersion = selectedEyeVersion ?? currentEyeVersion;

  const activeEyeVariants =
    eyeVersionOptions.find((v) => v.version === activeEyeVersion)?.platforms ?? [];

  // Which platforms this build was published for at all. One or none means
  // there is nothing to choose, so no control is drawn.
  const eyePlatformChoices = Array.from(
    new Set(
      activeEyeVariants
        .map((v) => parseEyeVariant(v).platform)
        .filter((p): p is EyePlatform => p !== null)
    )
  );

  // One tile per host. For a host published on several platforms, take the
  // chosen one; for a host published on exactly one, take it and say which,
  // so nothing the server offered becomes unreachable through the UI.
  const eyeTiles = (() => {
    const byHost = new Map<
      string,
      { variant: string; platform: EyePlatform | null }[]
    >();
    for (const variant of activeEyeVariants) {
      const { host, platform } = parseEyeVariant(variant);
      byHost.set(host, [...(byHost.get(host) ?? []), { variant, platform }]);
    }
    const ordered = [
      ...EYE_HOST_ORDER.filter((h) => byHost.has(h)),
      ...Array.from(byHost.keys()).filter((h) => !EYE_HOST_ORDER.includes(h))
    ];
    return ordered.map((host) => {
      const built = byHost.get(host) ?? [];
      const match = built.find((b) => b.platform === eyePlatform) ?? built[0];
      const name =
        host === 'product'
          ? t('management.eye.downloadFull')
          : (EYE_HOST_LABEL[host] ?? host);
      // Name the platform only when it is not the one already chosen above,
      // which is the case where the label would otherwise mislead.
      const label =
        match.platform && match.platform !== eyePlatform
          ? `${name} (${EYE_PLATFORM_LABEL[match.platform]})`
          : name;
      return { host, variant: match.variant, label };
    });
  })();

  const eulaQuery = useQuery({
    queryKey: ['eye-eula'],
    queryFn: async () => managementService.getEyeEulaStatus()
  });
  const [eulaAgreed, setEulaAgreed] = useState(false);
  const acceptEula = useMutation({
    mutationFn: () => managementService.acceptEyeEula(EYE_EULA_VERSION),
    onSuccess: () => eulaQuery.refetch(),
    onError: () => toast.error(t('management.eye.eulaError'))
  });

  const handleDownload = (variant?: string, version?: string) =>
    downloads.run(`${version ?? 'current'}-${variant ?? 'product'}`, () =>
      doDownload(variant, version)
    );

  const doDownload = async (variant?: string, version?: string) => {
    const response = await fetch(
      managementService.getEyePackageUrl(variant, version),
      { credentials: 'include' }
    );
    if (response.status === 403) {
      // The endpoint 403s with a machine-readable code when the (current)
      // EULA hasn't been accepted — e.g. the version was bumped since this
      // page loaded. Refetching flips the card back to the accept prompt.
      const body = await response.json().catch(() => null);
      if (body?.code === 'EYE_EULA_ACCEPTANCE_REQUIRED') {
        eulaQuery.refetch();
        // Tell them WHICH of the two situations this is. `eulaChanged` means
        // they had accepted, and the terms moved since - a very different
        // message from a first-run "please accept", and the one someone who
        // accepted last week needs in order to not read this as a bug.
        toast.error(
          body?.reason === 'EULA_CHANGED'
            ? t('management.eye.eulaChanged')
            : t('management.eye.eulaRequired')
        );
      } else {
        toast.error(t('management.eye.forbidden'));
      }
      return;
    }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition');
    // The server names the file, version and all. The fallback only runs if
    // Content-Disposition is missing, and it used to say "full" for every
    // download - so a 84 KB Blender archive saved as squarebit-eye-full.zip.
    const filename =
      disposition?.match(/filename="?([^";]+)"?/i)?.[1] ??
      `squarebiteye_${version ?? currentEyeVersion ?? 'latest'}_${
        variant ?? 'product'
      }.zip`;
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
    toast.success(t('download.started'));
  };

  return (
    <div className="w-full rounded-[18px] border border-border-dark/[0.11] bg-surface-base shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)] overflow-hidden relative">
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue to-[#6d5fc0] rounded-t-[18px]" />
      <div className="px-4 md:px-[30px] pt-[26px] pb-[22px] border-b border-border-dark/[0.07]">
        <div className="flex items-center gap-[10px] flex-wrap">
          <h2 className="text-[19px] font-display font-bold text-content-dark tracking-tight">
            Squarebit Eye
          </h2>
          {version && (
            <Link
              to={ROUTER_LINKS.SQUAREBIT_EYE_CHANGELOG}
              className="inline-flex items-center gap-[5px] px-[9px] py-[2px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide cursor-pointer hover:bg-surface-hover-section transition-colors"
            >
              <span className="w-[5px] h-[5px] rounded-full bg-blue animate-pulse" />
              {t('management.product.version', { version })}
            </Link>
          )}
          <Link
            to={ROUTER_LINKS.SQUAREBIT_EYE_DOCS}
            className="inline-flex items-center px-[9px] py-[2px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide uppercase cursor-pointer hover:bg-surface-hover-section transition-colors"
          >
            {t('management.eye.docs')}
          </Link>
        </div>
      </div>
      <div className="px-[30px] py-[22px]">
        {eulaQuery.isLoading ? (
          <div className="flex justify-center items-center py-[12px]">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          </div>
        ) : eulaQuery.data?.accepted ? (
          <>
            <div className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-content-muted-light mb-[13px]">
              {t('management.download')}
            </div>
            {/* Version and platform are chosen ONCE, above the grid; the
                grid is one tile per host. That ordering follows what people
                are actually doing: picking a host is the task, the platform is
                a fact about their machine, and rolling back a version is rare.

                Each control appears only when it has something to offer - one
                published version means no version select, one platform means
                no platform choice - so today this is a plain grid of hosts and
                nothing else. */}
            {(eyeVersionOptions.length > 1 || eyePlatformChoices.length > 1) && (
              <div className="flex gap-[12px] flex-wrap items-end mb-[13px]">
                {eyeVersionOptions.length > 1 && (
                  <label className="flex items-center gap-[7px]">
                    <span className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-content-muted-light">
                      {t('management.eye.chooseVersion')}
                    </span>
                    <select
                      value={activeEyeVersion ?? ''}
                      // Swapping the grid out from under an in-flight download
                      // would unmount its spinner while the toast kept running.
                      disabled={downloads.isDownloading}
                      onChange={(e) => setSelectedEyeVersion(e.target.value)}
                      className="font-mono text-[12px] px-[9px] py-[4px] rounded-[7px] border border-border-dark/[0.15] bg-surface-secondary-alt text-content-dark cursor-pointer disabled:opacity-60"
                    >
                      {eyeVersionOptions.map((v) => (
                        <option key={v.version} value={v.version}>
                          v{v.version}
                          {v.version === currentEyeVersion
                            ? ` (${t('management.eye.latest')})`
                            : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {eyePlatformChoices.length > 1 && (
                  <fieldset
                    disabled={downloads.isDownloading}
                    className="flex items-center gap-[6px] border-0 p-0 m-0"
                  >
                    <legend className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-content-muted-light float-left mr-[7px]">
                      {t('management.eye.choosePlatform')}
                    </legend>
                    {eyePlatformChoices.map((plat) => (
                      <label
                        key={plat}
                        className={`inline-flex items-center gap-[5px] px-[10px] py-[4px] rounded-[999px] text-[12px] font-medium border cursor-pointer transition-all ${
                          eyePlatform === plat
                            ? 'border-blue text-blue bg-surface-hover'
                            : 'border-border-dark/[0.15] text-content-muted bg-surface-secondary-alt hover:border-blue'
                        }`}
                      >
                        <input
                          type="radio"
                          name="eye-platform"
                          value={plat}
                          checked={eyePlatform === plat}
                          onChange={() => setEyePlatform(plat)}
                          className="sr-only"
                        />
                        {EYE_PLATFORM_LABEL[plat]}
                      </label>
                    ))}
                  </fieldset>
                )}
              </div>
            )}
            {versionsQuery.data?.githubReachable === false ? (
              <p className="font-body text-[12.5px] text-content-muted-alt">
                {t('management.eye.versionsUnavailable')}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-[10px]">
                {eyeTiles.map(({ host, variant, label }) => {
                  const Icon = EYE_HOST_ICON[host] ?? FiBox;
                  return (
                    <ButtonContainer
                      key={`${activeEyeVersion}-${variant}`}
                      label={label}
                      onDownload={() =>
                        handleDownload(
                          variant,
                          activeEyeVersion === currentEyeVersion
                            ? undefined
                            : (activeEyeVersion ?? undefined)
                        )
                      }
                    >
                      <Icon size={18} color="var(--color-text-muted-alt)" />
                    </ButtonContainer>
                  );
                })}
                {/* Before the list arrives, and if it never does, the complete
                    bundle stays reachable - it is the server's default
                    variant, so this is the request this card always made. */}
                {eyeTiles.length === 0 && (
                  <ButtonContainer
                    label={t('management.eye.downloadFull')}
                    onDownload={() => handleDownload()}
                  >
                    <FiPackage size={18} color="var(--color-text-muted-alt)" />
                  </ButtonContainer>
                )}
              </div>
            )}
          </>
        ) : (
          // First-download EULA clickwrap — same pattern as the Subdivs free
          // claim: explicit checkbox (separate from any other agreement) +
          // Accept button; the full text opens in a new tab.
          <>
            <p className="font-body text-[14px] text-content-secondary leading-[1.6] mb-[16px]">
              {t('management.eye.eulaPrompt')}
            </p>
            <label className="flex items-start gap-[8px] cursor-pointer select-none mb-[16px]">
              <input
                type="checkbox"
                className="mt-[2px] h-[16px] w-[16px] shrink-0 accent-blue-600 cursor-pointer"
                checked={eulaAgreed}
                onChange={(e) => setEulaAgreed(e.target.checked)}
              />
              <span className="font-body text-[13px] text-content-secondary leading-[1.5]">
                <Trans
                  i18nKey="management.eye.eulaAgree"
                  components={[
                    <a
                      href="/squarebit-eye/eula"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue underline hover:text-surface-popular"
                    >
                      Squarebit Eye EULA
                    </a>
                  ]}
                />
              </span>
            </label>
            <button
              type="button"
              disabled={!eulaAgreed || acceptEula.isPending}
              onClick={() => acceptEula.mutate()}
              className={`inline-flex items-center justify-center py-[11px] px-[24px] rounded-[9px] text-[13px] font-semibold bg-blue border-[1.5px] border-blue text-surface-base shadow-[0_2px_10px_rgba(14,165,201,0.3)] transition-all duration-200 ${!eulaAgreed || acceptEula.isPending ? 'opacity-50 cursor-not-allowed' : 'hover:bg-surface-popular hover:border-surface-popular cursor-pointer'}`}
            >
              {acceptEula.isPending
                ? t('management.eye.eulaAccepting')
                : t('management.eye.eulaAccept')}
            </button>
          </>
        )}
        <p className="mt-[16px] font-body italic text-[12px] text-content-muted-alt leading-[1.5]">
          {t('management.eye.grantedBy')}
        </p>
        {/* Licence (may RUN) is separate from the grant + EULA above (may
            DOWNLOAD). Renders nothing at all unless this account holds one. */}
        <EyeLicenseSection />
      </div>
    </div>
  );
};

// Invite-only Squarebit Framework card - rendered ONLY for accounts holding an
// admin-granted 'sbframework' entitlement. Same shape as the Eye card: an EULA
// clickwrap gates the first download, and the server remembers the acceptance
// so later visits go straight to the download buttons.
//
// The difference from Eye is the licence table underneath. The framework is
// licensed by LicenseSpring, so granting it links the account to a `mix` key
// (adopting the one that email already holds where there is one). That key is
// what the Maya plugin's License Activation dialog wants, so it is shown here
// rather than in the generic per-product card.
const SquarebitFrameworkCard = ({ productKeys }: { productKeys: Ikey[] }) => {
  const { t } = useTranslation();
  const version = pkg.sbframework?.version;
  // The editor is offered from this card too, and has a version of its own:
  // it is a separate project on its own release cadence, granted with the
  // framework rather than part of it.
  const nxt = useNxtVersion();

  const eulaQuery = useQuery({
    queryKey: ['sbframework-eula'],
    queryFn: async () => managementService.getFrameworkEulaStatus()
  });

  // Older builds, for anyone pinned to a Maya version or rolling back. Kept
  // out of the main download row on purpose: almost everyone wants current,
  // and a wall of versions above the button would bury it.
  const versionsQuery = useQuery({
    queryKey: ['sbframework-versions'],
    queryFn: async () => managementService.getFrameworkVersions()
  });
  const [showOlder, setShowOlder] = useState(false);
  // The versions the changelog documents. The release list is every build ever
  // published, most of them predating the changelog or superseded within a day,
  // and offering those invites people to install something nobody can tell them
  // anything about.
  const changelogVersionsQuery = useQuery({
    queryKey: ['sbframework-changelog-versions'],
    queryFn: async () => fetchChangelogVersions('/changelog-sbframework.md')
  });
  const documented = changelogVersionsQuery.data;
  const olderVersions = (versionsQuery.data?.versions ?? []).filter(
    (entry) =>
      entry.version !== version &&
      // A changelog that could not be read means no opinion, not "nothing is
      // documented": a missing file must not empty the download list.
      (!documented || documented.includes(entry.version))
  );
  // Older builds live only on the releases, so when those cannot be reached
  // they cannot be downloaded either. Say that rather than render an empty
  // section that reads as "there are none". The current build is mirrored onto
  // the server, so it is unaffected and the notice says so.
  const olderUnavailable =
    versionsQuery.isSuccess && versionsQuery.data?.githubReachable === false;
  const [eulaAgreed, setEulaAgreed] = useState(false);
  const acceptEula = useMutation({
    mutationFn: () =>
      managementService.acceptFrameworkEula(SBFRAMEWORK_EULA_VERSION),
    onSuccess: () => eulaQuery.refetch(),
    onError: () => toast.error(t('management.framework.eulaError'))
  });

  const downloads = useDownloadTracker();

  const handleDownload = (os: string, downloadVersion?: string) =>
    downloads.run(`${downloadVersion ?? 'current'}-${os}`, async () => {
      await doDownload(os, downloadVersion);
    });

  const doDownload = async (os: string, downloadVersion?: string) => {
    const response = await fetch(
      managementService.getFrameworkPackageUrl(os, downloadVersion),
      { credentials: 'include' }
    );
    if (response.status === 403) {
      // The endpoint 403s with a machine-readable code when the CURRENT EULA
      // has not been accepted - e.g. the version was bumped since this page
      // loaded. Refetching flips the card back to the accept prompt.
      const body = await response.json().catch(() => null);
      if (body?.code === 'SBFRAMEWORK_EULA_ACCEPTANCE_REQUIRED') {
        eulaQuery.refetch();
        // See the Eye handler: re-acceptance after a change reads differently
        // from a first acceptance, so it gets its own message.
        toast.error(
          body?.reason === 'EULA_CHANGED'
            ? t('management.framework.eulaChanged')
            : t('management.framework.eulaRequired')
        );
      } else {
        toast.error(t('management.framework.forbidden'));
      }
      return;
    }
    if (!response.ok) {
      toast.error(t('management.framework.downloadError'));
      return;
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition');
    const filename =
      disposition?.match(/filename="?([^";]+)"?/i)?.[1] ??
      (downloadVersion
        ? `sbframework_${downloadVersion}_${os}.zip`
        : `sbframework_${os}.zip`);
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
    toast.success(t('download.started'));
  };

  return (
    <div className="w-full rounded-[18px] border border-border-dark/[0.11] bg-surface-base shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)] overflow-hidden relative">
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue to-[#6d5fc0] rounded-t-[18px]" />
      <div className="px-4 md:px-[30px] pt-[26px] pb-[22px] border-b border-border-dark/[0.07]">
        <div className="flex items-center gap-[10px] flex-wrap">
          <h2 className="text-[19px] font-display font-bold text-content-dark tracking-tight">
            {FRAMEWORK_PRODUCT_NAME}
          </h2>
          {version && (
            <Link
              to={ROUTER_LINKS.SBFRAMEWORK_CHANGELOG}
              className="inline-flex items-center gap-[5px] px-[9px] py-[2px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide cursor-pointer hover:bg-surface-hover-section transition-colors"
            >
              <span className="w-[5px] h-[5px] rounded-full bg-blue animate-pulse" />
              {t('management.product.version', { version })}
            </Link>
          )}
          <Link
            to={ROUTER_LINKS.SBFRAMEWORK_DOCS}
            className="inline-flex items-center px-[9px] py-[2px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide uppercase cursor-pointer hover:bg-surface-hover-section transition-colors"
          >
            {t('management.framework.docs')}
          </Link>
        </div>
      </div>
      <div className="px-[30px] py-[22px]">
        {eulaQuery.isLoading ? (
          <div className="flex justify-center items-center py-[12px]">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          </div>
        ) : eulaQuery.data?.accepted ? (
          <>
            <div className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-content-muted-light mb-[13px]">
              {t('management.download')}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-[10px]">
              <ButtonContainer
                label="Windows"
                onDownload={() => handleDownload('windows')}
              >
                <FaWindows size={18} color="var(--color-text-muted-alt)" />
              </ButtonContainer>
              <ButtonContainer
                label="macOS"
                onDownload={() => handleDownload('mac')}
              >
                <FaApple size={18} color="var(--color-text-muted-alt)" />
              </ButtonContainer>
            </div>
          </>
        ) : (
          <>
            <p className="font-body text-[14px] text-content-secondary leading-[1.6] mb-[16px]">
              {t('management.framework.eulaPrompt')}
            </p>
            <label className="flex items-start gap-[8px] cursor-pointer select-none mb-[16px]">
              <input
                type="checkbox"
                className="mt-[2px] h-[16px] w-[16px] shrink-0 accent-blue-600 cursor-pointer"
                checked={eulaAgreed}
                onChange={(e) => setEulaAgreed(e.target.checked)}
              />
              <span className="font-body text-[13px] text-content-secondary leading-[1.5]">
                <Trans
                  i18nKey="management.framework.eulaAgree"
                  components={[
                    <a
                      href="/sbframework/eula"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue underline hover:text-surface-popular"
                    >
                      Squarebit Framework EULA
                    </a>
                  ]}
                />
              </span>
            </label>
            <button
              type="button"
              disabled={!eulaAgreed || acceptEula.isPending}
              onClick={() => acceptEula.mutate()}
              className={`inline-flex items-center justify-center py-[11px] px-[24px] rounded-[9px] text-[13px] font-semibold bg-blue border-[1.5px] border-blue text-surface-base shadow-[0_2px_10px_rgba(14,165,201,0.3)] transition-all duration-200 ${!eulaAgreed || acceptEula.isPending ? 'opacity-50 cursor-not-allowed' : 'hover:bg-surface-popular hover:border-surface-popular cursor-pointer'}`}
            >
              {acceptEula.isPending
                ? t('management.framework.eulaAccepting')
                : t('management.framework.eulaAccept')}
            </button>
          </>
        )}

        {/* Older builds. Only rendered once the EULA is accepted, because the
            accept prompt occupies this space until then and the downloads
            would 403 anyway. */}
        {eulaQuery.data?.accepted && olderUnavailable && (
          <div className="mt-[14px] rounded-[10px] border border-amber-500/30 bg-amber-500/[0.07] px-[14px] py-[11px]">
            <p className="font-body text-[13px] leading-[1.6] text-content-secondary m-0">
              {t('management.framework.olderUnavailable')}
            </p>
          </div>
        )}
        {eulaQuery.data?.accepted &&
          !olderUnavailable &&
          olderVersions.length > 0 && (
            <div className="mt-[20px]">
              {/* A bordered control rather than a bare label: this is the only
                way to reach an older build, and as small grey text nobody
                found it. */}
              <button
                type="button"
                onClick={() => setShowOlder((open) => !open)}
                aria-expanded={showOlder}
                className={`w-full inline-flex items-center gap-[9px] px-[14px] py-[10px] rounded-[11px] border text-[13px] font-medium transition-all cursor-pointer ${
                  showOlder
                    ? 'border-blue/[0.35] bg-surface-hover text-blue'
                    : 'border-border-dark/[0.14] bg-surface-section text-content-secondary hover:border-blue hover:text-blue hover:bg-surface-hover'
                }`}
              >
                <FiClock size={15} className="shrink-0" />
                <span>
                  {t('management.framework.olderVersions', {
                    count: olderVersions.length
                  })}
                </span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                  className={`ml-auto shrink-0 transition-transform ${showOlder ? 'rotate-180' : ''}`}
                >
                  <path
                    d="M3.5 5.25L7 8.75l3.5-3.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {showOlder && (
                <div className="mt-[12px] flex flex-col gap-[8px]">
                  {olderVersions.map((entry) => (
                    <div
                      key={entry.version}
                      className="flex items-center justify-between gap-[12px] px-[12px] py-[9px] rounded-[10px] border border-border-dark/[0.11]"
                    >
                      <span className="font-mono text-[12px] text-content-secondary">
                        v{entry.version}
                      </span>
                      <div className="flex items-center gap-[6px]">
                        {entry.platforms.includes('windows') && (
                          <button
                            type="button"
                            disabled={
                              downloads.busyKey === `${entry.version}-windows`
                            }
                            onClick={() => handleDownload('windows', entry.version)}
                            title={t('changelog.downloadFor', {
                              platform: 'Windows',
                              version: entry.version
                            })}
                            className={`group inline-flex items-center gap-[6px] px-[11px] py-[5px] rounded-[999px] text-[12px] font-medium bg-surface-secondary-alt border border-border-dark/[0.15] text-content-muted transition-all ${
                              downloads.busyKey === `${entry.version}-windows`
                                ? 'opacity-50 cursor-wait'
                                : 'hover:border-blue hover:text-blue hover:bg-surface-hover cursor-pointer'
                            }`}
                          >
                            <FaWindows size={12} />
                            Windows
                            <DownloadArrow />
                          </button>
                        )}
                        {entry.platforms.includes('mac') && (
                          <button
                            type="button"
                            disabled={downloads.busyKey === `${entry.version}-mac`}
                            onClick={() => handleDownload('mac', entry.version)}
                            title={t('changelog.downloadFor', {
                              platform: 'macOS',
                              version: entry.version
                            })}
                            className={`group inline-flex items-center gap-[6px] px-[11px] py-[5px] rounded-[999px] text-[12px] font-medium bg-surface-secondary-alt border border-border-dark/[0.15] text-content-muted transition-all ${
                              downloads.busyKey === `${entry.version}-mac`
                                ? 'opacity-50 cursor-wait'
                                : 'hover:border-blue hover:text-blue hover:bg-surface-hover cursor-pointer'
                            }`}
                          >
                            <FaApple size={12} />
                            macOS
                            <DownloadArrow />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        {/* Its own section rather than a third platform button. The graph
            editor the templates are authored and built in: granted with the
            framework and covered by the same EULA, so it goes through the same
            gated endpoint, but a separate product with its own releases - and
            somewhere to hang its own version list later. */}
        {eulaQuery.data?.accepted && (
          <div className="mt-[24px] pt-[20px] border-t border-border-dark/[0.09]">
            <div className="flex items-center gap-[10px] mb-[13px]">
              <div className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-content-muted-light">
                {t('management.framework.nxtEditor')}
              </div>
              {/* The same pill the product cards use, and a link for the same
                  reason: the version is the thing people look for, so it is
                  the thing worth making clickable. Absent until the changelog
                  has been read, rather than showing a number we are guessing
                  at. */}
              {nxt.version && (
                <Link
                  to={ROUTER_LINKS.SBFRAMEWORK_NXT_CHANGELOG}
                  className="inline-flex items-center gap-[5px] px-[9px] py-[2px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide cursor-pointer hover:bg-surface-hover-section transition-colors"
                >
                  <span className="w-[5px] h-[5px] rounded-full bg-blue animate-pulse" />
                  {t('management.product.version', { version: nxt.version })}
                </Link>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-[10px]">
              <ButtonContainer
                label={t('management.framework.nxtEditorDownload')}
                onDownload={() => handleDownload('nxt')}
              >
                <FiGitBranch size={18} color="var(--color-text-muted-alt)" />
              </ButtonContainer>
            </div>
            <p className="mt-[10px] font-body italic text-[12px] text-content-muted-alt leading-[1.5]">
              {t('management.framework.nxtEditorNote')}
            </p>
          </div>
        )}

        {/* The LicenseSpring key the Maya plugin activates against. Absent only
            when the grant could not be keyed (an admin-visible failure), so the
            table is skipped rather than shown empty. */}
        {productKeys.length > 0 && (
          <div className="mt-[24px] pt-[22px] border-t border-border-dark/[0.07]">
            <ManagementTable data={productKeys} />
          </div>
        )}

        <p className="mt-[16px] font-body italic text-[12px] text-content-muted-alt leading-[1.5]">
          {t('management.framework.grantedBy')}
        </p>
      </div>
    </div>
  );
};

// Shown when a user arrives with a claim intent (Subdivs page "Get Started Free" button or
// the docs link) but hasn't claimed yet. Accepting the Subdivs EULA (clickwrap) is required
// before the free entitlement is claimed — parity with the paid checkout consent.
const SubdivsFreeClaimCard = ({ onClaimed }: { onClaimed: () => void }) => {
  const { t } = useTranslation();
  const [agreed, setAgreed] = useState(false);
  const claim = useMutation({
    mutationFn: () => homeService.claimSubdivsFree(SUBDIVS_EULA_VERSION),
    onSuccess: () => {
      try {
        localStorage.removeItem('claimSubdivsFree');
      } catch {
        /* ignore */
      }
      onClaimed();
    },
    onError: () => toast.error(t('management.subdivs.claimError'))
  });
  return (
    <div className="w-full rounded-[18px] border border-border-dark/[0.11] bg-surface-base shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)] overflow-hidden relative">
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue to-[#6d5fc0] rounded-t-[18px]" />
      <div className="px-4 md:px-[30px] pt-[26px] pb-[22px] border-b border-border-dark/[0.07]">
        <div className="flex items-center gap-[10px] flex-wrap">
          <h2 className="text-[19px] font-display font-bold text-content-dark tracking-tight">
            Squarebit Subdivs
          </h2>
          <span className="inline-flex items-center px-[9px] py-[2px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide uppercase">
            Free
          </span>
        </div>
      </div>
      <div className="px-[30px] py-[22px]">
        <p className="font-body text-[14px] text-content-secondary leading-[1.6] mb-[16px]">
          {t('management.subdivsFreeNote')}
        </p>
        <label className="flex items-start gap-[8px] cursor-pointer select-none mb-[16px]">
          <input
            type="checkbox"
            className="mt-[2px] h-[16px] w-[16px] shrink-0 accent-blue-600 cursor-pointer"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          <span className="font-body text-[13px] text-content-secondary leading-[1.5]">
            <Trans
              i18nKey="management.subdivs.eulaAgree"
              components={[
                <a
                  href="/squarebit-subdivs/eula"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue underline hover:text-surface-popular"
                >
                  Squarebit Subdivs EULA
                </a>
              ]}
            />
          </span>
        </label>
        <button
          type="button"
          disabled={!agreed || claim.isPending}
          onClick={() => claim.mutate()}
          className={`inline-flex items-center justify-center py-[11px] px-[24px] rounded-[9px] text-[13px] font-semibold bg-blue border-[1.5px] border-blue text-surface-base shadow-[0_2px_10px_rgba(14,165,201,0.3)] transition-all duration-200 ${!agreed || claim.isPending ? 'opacity-50 cursor-not-allowed' : 'hover:bg-surface-popular hover:border-surface-popular cursor-pointer'}`}
        >
          {claim.isPending
            ? t('management.subdivs.claiming')
            : t('management.subdivs.claimCta')}
        </button>
      </div>
    </div>
  );
};

export const ManagementContainer = () => {
  const { t } = useTranslation();
  const keysQuery = useQuery({
    queryKey: ['keys'],
    queryFn: async () => managementService.getLicenseKeys()
  });

  useEffect(() => {
    if (keysQuery.error) {
      toast.error(t('management.errors.getLicenseKeys'));
    }
  }, [keysQuery.error]);

  const data = keysQuery.data!;
  const succesCond = keysQuery.isSuccess && (data as Ikey[])?.length !== 0;

  const hasPortalQuery = useQuery({
    queryKey: ['portal'],
    queryFn: async () => settingsService.getUserHasPortal()
  });

  // Admin-granted entitlements (invite-only products, e.g. the full Squarebit
  // Eye package). Shared query key with the Settings page so it's cached once.
  const accessQuery = useQuery({
    queryKey: ['my-access'],
    queryFn: async () => managementService.getMyAccess()
  });
  const hasEyeAccess = !!accessQuery.data?.includes(EYE_PRODUCT_SLUG);
  const { granted: hasFrameworkAccess } = useHasFrameworkAccess();

  useEffect(() => {
    const timer = setTimeout(() => {
      keysQuery.refetch();
    }, 1500);

    return () => {
      clearTimeout(timer);
    };
  }, [succesCond]);

  // Group the user's PAID keys by product so each renders its own card (header +
  // version + download + that product's keys). The keyless Subdivs free entitlement
  // (type 'free') is handled separately — it has no real key to show in the table.
  const keysByProduct =
    keysQuery.isSuccess && data?.length
      ? (data as Ikey[])
          .filter((key) => key.type !== 'free')
          // The framework renders its own grant-gated card (download + EULA +
          // keys), so it must not also appear as a generic product card.
          .filter((key) => key.productName !== FRAMEWORK_PRODUCT_NAME)
          .reduce<Record<string, Ikey[]>>((acc, key) => {
            const name = key.productName ?? 'Unknown';
            (acc[name] ??= []).push(key);
            return acc;
          }, {})
      : {};

  const frameworkKeys =
    keysQuery.isSuccess && data?.length
      ? (data as Ikey[]).filter((key) => key.productName === FRAMEWORK_PRODUCT_NAME)
      : [];

  const hasSubdivsPaid = !!keysByProduct['Squarebit Subdivs'];
  const hasSubdivsFree =
    keysQuery.isSuccess &&
    !!(data as Ikey[])?.some(
      (k) => k.type === 'free' && k.productName === 'Squarebit Subdivs'
    );
  const ownsSubdivs = hasSubdivsPaid || hasSubdivsFree;

  // A free-tier claim intent arrives via the ?getSubdivs=1 query (Subdivs page "Get
  // Started Free" button or the docs link) or the deferred localStorage flag (set before
  // a logged-out signup). Either way we show the EULA-consent claim card below rather than
  // auto-claiming, so the user explicitly accepts the EULA (clickwrap) first.
  const [searchParams] = useSearchParams();
  const [wantsToClaim, setWantsToClaim] = useState(false);
  useEffect(() => {
    if (searchParams.get('getSubdivs') === '1') {
      setWantsToClaim(true);
      return;
    }
    try {
      if (localStorage.getItem('claimSubdivsFree') === '1') setWantsToClaim(true);
    } catch {
      /* ignore */
    }
  }, [searchParams]);

  // The Eye's free tier arrives the same way: ?getEye=1 from the Free card, or
  // the deferred flag set before a logged-out signup.
  //
  // Unlike Subdivs this DOES auto-claim, because the two products gate their
  // EULA in different places. Subdivs records acceptance at claim time, so it
  // has to ask first. The Eye gates the DOWNLOAD on acceptance
  // (EYE_EULA_ACCEPTANCE_REQUIRED), so claiming puts the licence in place and
  // the existing gate below asks before anything can actually be downloaded.
  // Asking twice would be theatre, and asking only at claim time would let a
  // licence claimed before a EULA revision escape the newer terms.
  const claimEye = useMutation({
    mutationFn: () => managementService.claimFreeEye(),
    onSuccess: () => {
      try {
        localStorage.removeItem('claimEyeFree');
      } catch {
        /* ignore */
      }
      // Refetch the access list so the Eye card appears without a reload.
      // Same pattern the Subdivs claim uses; this component refetches queries
      // directly rather than holding a queryClient.
      accessQuery.refetch();
    },
    onError: () => toast.error(t('management.eye.claimError'))
  });
  const eyeClaimFired = React.useRef(false);
  useEffect(() => {
    if (eyeClaimFired.current) return;
    let wants = searchParams.get('getEye') === '1';
    if (!wants) {
      try {
        wants = localStorage.getItem('claimEyeFree') === '1';
      } catch {
        /* ignore */
      }
    }
    if (!wants) return;

    // Wait until we know what this account already holds. Claiming before the
    // access list arrives is what turned a stale flag into a surprise licence.
    if (!accessQuery.isSuccess) return;

    // Once per mount. The effect re-runs on every searchParams change, and a
    // second POST would be harmless (the claim is idempotent) but would still
    // be a wasted round trip on every navigation.
    eyeClaimFired.current = true;

    // ONE SHOT. The flag used to be cleared only on SUCCESS, so it survived in
    // localStorage indefinitely: a click on the Free card while logged out -
    // which any visitor can now do, since the pricing cards are no longer
    // gated - would sit there and mint a licence on some later visit to this
    // page, with nothing connecting the two. Dropping it as soon as it is read
    // means the click either takes effect now or not at all, which is the only
    // version of this a person can reason about.
    try {
      localStorage.removeItem('claimEyeFree');
    } catch {
      /* ignore */
    }

    // Already have the Eye? Then there is nothing to claim. This matters
    // because ensureForGrant looks only for free/trial rows when issuing the
    // free tier, so an ADMIN-granted holder does not count as having one and
    // would be given a second, free key alongside the grant they already had.
    if (accessQuery.data?.includes(EYE_PRODUCT_SLUG)) return;

    claimEye.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, accessQuery.isSuccess, accessQuery.data]);

  return (
    <ManagememtContainer>
      {keysQuery.isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : keysQuery.isSuccess && data.length > 0 ? (
        <div className="flex flex-col gap-[20px]">
          {Object.entries(keysByProduct).map(([productName, productKeys]) => {
            const ui = PRODUCT_UI[productName];
            return (
              <div
                key={productName}
                className="w-full rounded-[18px] border border-border-dark/[0.11] bg-surface-base shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)] overflow-hidden relative"
              >
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue to-[#6d5fc0] rounded-t-[18px]" />

                {/* Product header */}
                <div className="px-4 md:px-[30px] pt-[26px] pb-[22px] border-b border-border-dark/[0.07] flex flex-col gap-4 md:flex-row md:gap-0 md:justify-between md:items-center">
                  <div>
                    <h2 className="text-[19px] font-display font-bold text-content-dark tracking-tight">
                      {productName}
                    </h2>
                    {ui?.version &&
                      (ui.changelogPath ? (
                        <Link
                          to={ui.changelogPath}
                          className="inline-flex items-center gap-[5px] mt-[6px] px-[9px] py-[2px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide cursor-pointer hover:bg-surface-hover-section transition-colors"
                        >
                          <span className="w-[5px] h-[5px] rounded-full bg-blue animate-pulse" />
                          {t('management.product.version', { version: ui.version })}
                        </Link>
                      ) : (
                        <span className="inline-flex items-center gap-[5px] mt-[6px] px-[9px] py-[2px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide">
                          <span className="w-[5px] h-[5px] rounded-full bg-blue animate-pulse" />
                          {t('management.product.version', { version: ui.version })}
                        </span>
                      ))}
                  </div>
                  {!!hasPortalQuery.data && (
                    <PortalButton className="w-full md:w-auto md:justify-end" />
                  )}
                </div>

                {/* Download section — only for products we know how to serve */}
                {ui && (
                  <div className="px-[30px] py-[22px] border-b border-border-dark/[0.07]">
                    {ui.download === 'squarebit-subdivs' ? (
                      <SubdivsDownload />
                    ) : (
                      <FileLoad />
                    )}
                  </div>
                )}

                {/* License keys section */}
                <div className="px-[30px] py-[22px] pb-[26px]">
                  <ManagementTable data={productKeys} />
                </div>
              </div>
            );
          })}
          {hasSubdivsFree && !hasSubdivsPaid && (
            <SubdivsFreeCard version={pkg.squarebitSubdivs?.version} />
          )}
          {hasEyeAccess && <SquarebitEyeCard />}
          {hasFrameworkAccess && (
            <SquarebitFrameworkCard productKeys={frameworkKeys} />
          )}
          {wantsToClaim && !ownsSubdivs && (
            <SubdivsFreeClaimCard
              onClaimed={() => {
                setWantsToClaim(false);
                keysQuery.refetch();
              }}
            />
          )}
        </div>
      ) : keysQuery.isSuccess && data.length === 0 ? (
        wantsToClaim || hasEyeAccess || hasFrameworkAccess ? (
          <div className="flex flex-col gap-[20px]">
            {hasEyeAccess && <SquarebitEyeCard />}
            {hasFrameworkAccess && (
              <SquarebitFrameworkCard productKeys={frameworkKeys} />
            )}
            {wantsToClaim && (
              <SubdivsFreeClaimCard
                onClaimed={() => {
                  setWantsToClaim(false);
                  keysQuery.refetch();
                }}
              />
            )}
          </div>
        ) : (
          // No products yet (no purchase and no free claim) — nudge to the product pages.
          <div className="flex flex-col items-center">
            <InformSpan className="mt-[24px] text-center">
              {t('management.noKeys.message')}
            </InformSpan>
            <div className="flex flex-col sm:flex-row gap-[12px] mt-[18px] mb-[24px]">
              {PRODUCT_PAGES.map((p) => (
                <Link
                  key={p.path}
                  to={p.path}
                  className="flex items-center justify-center py-[11px] px-[24px] rounded-[9px] text-[13px] font-semibold bg-blue border-[1.5px] border-blue text-surface-base shadow-[0_2px_10px_rgba(14,165,201,0.3)] hover:bg-surface-popular hover:border-surface-popular transition-all duration-200 no-underline"
                >
                  {t('management.explore', { product: p.name })}
                </Link>
              ))}
            </div>
          </div>
        )
      ) : null}

      {/* Slim cross-sell for owners: link only the products they DON'T own. */}
      {keysQuery.isSuccess && data.length > 0 && (
        <>
          {(() => {
            const notOwned = PRODUCT_PAGES.filter(
              (p) =>
                !keysByProduct[p.name] &&
                // Subdivs is shown as its own card above when the user owns it (free or paid).
                !(p.name === 'Squarebit Subdivs' && ownsSubdivs)
            );
            if (notOwned.length === 0) return null;
            return (
              <p className="w-full mt-[18px] font-body text-[13px] text-content-muted-alt text-center">
                {t('management.exploreMore')}{' '}
                {notOwned.map((p, i) => (
                  <React.Fragment key={p.path}>
                    {i > 0 && ' · '}
                    <Link
                      to={p.path}
                      className="text-blue underline hover:text-surface-popular"
                    >
                      {p.name}
                    </Link>
                  </React.Fragment>
                ))}
              </p>
            );
          })()}
        </>
      )}
    </ManagememtContainer>
  );
};
